import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useLayoutEffect, useMemo, useRef } from 'react';
import type { FC, JSX, Ref } from 'react';

type Props = Omit<JSX.IntrinsicElements['div'], 'onResize'> & {
  containerRef?: Ref<HTMLDivElement>; // disallow legacy ref to be able to mergeRefs
  onResize: (element: HTMLDivElement) => void;
};

export const ResizeObserverContainer: FC<Props> = ({
  onResize,
  containerRef,
  ...otherProps
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const handleResize = useStableCallback((entry: ResizeObserverEntry) =>
    onResize(entry.target as HTMLDivElement),
  );
  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach(handleResize);
      }),
    [],
  );

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el) {
      stumble('ResizeObserverContainer: observed div ref not available');
      return undefined;
    }
    resizeObserver.observe(el);
    return () => {
      resizeObserver.unobserve(el);
    };
  }, [resizeObserver]);

  return (
    <div
      {...otherProps}
      ref={containerRef ? mergeRefs(containerRef, elementRef) : elementRef}
    />
  );
};
